import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useMemo, useRef } from "react";
import { useTabScroller } from "../hooks/useTabScroller";

type TabViewsProps = {
  view: string | null;
  userViews: { name: string; path: string }[];
};

export default function TabViews({ view, userViews }: TabViewsProps) {
  const names = useMemo(() => userViews.map((v) => v.name), [userViews]);

  const ref = useRef<HTMLUListElement>(null);
  const { scrollByStep, startHold, stopHold, hasOverflow, atStart, atEnd } =
    useTabScroller(ref);

  return (
    <div className="relative border-b bg-white">
      {/* Fixed side buttons */}
      {!atStart && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          onClick={() => scrollByStep("left")}
          onMouseDown={() => startHold("left")}
          onMouseUp={stopHold}
          onMouseLeave={stopHold}
          onTouchStart={() => startHold("left")}
          onTouchEnd={stopHold}
          onTouchCancel={stopHold}
          disabled={!hasOverflow || atStart}
          className={cn(
            "absolute left-2 top-1/2 -translate-y-1/2 z-10",
            "h-8 w-8 rounded-md border bg-white shadow-sm",
            "hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed",
          )}
        >
          ‹
        </button>
      )}

      {!atEnd && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          onClick={() => scrollByStep("right")}
          onMouseDown={() => startHold("right")}
          onMouseUp={stopHold}
          onMouseLeave={stopHold}
          onTouchStart={() => startHold("right")}
          onTouchEnd={stopHold}
          onTouchCancel={stopHold}
          disabled={!hasOverflow || atEnd}
          className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2 z-10",
            "h-8 w-8 rounded-md border bg-white shadow-sm",
            "hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed",
          )}
        >
          ›
        </button>
      )}

      {/* Scrolling row */}
      <div className="mx-12 h-14 flex items-center">
        <div className="relative w-full">
          {/* edge fades, under buttons */}
          {hasOverflow && !atStart && (
            <div className="pointer-events-none absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-white to-transparent z-[1]" />
          )}
          {hasOverflow && !atEnd && (
            <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent z-[1]" />
          )}

          <ul
            ref={ref}
            className={cn(
              "flex items-center gap-1 overflow-x-auto no-scrollbar pr-1 h-14 text-xs",
              "min-w-full", // prend toute la largeur
              !hasOverflow && "justify-center", // centre seulement si ça ne déborde pas
            )}
            style={{ scrollBehavior: "smooth" }}
          >
            {names.map((name) => (
              <TabView key={name} view={view} userView={name} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

type TabViewProps = { view: string | null; userView: string };
function TabView({ view, userView }: TabViewProps) {
  const isActive = view === userView;
  return (
    <li
      className={cn(
        "flex items-center text-sm text-slate-700 cursor-pointer p-4 border-b-4 whitespace-nowrap",
        isActive
          ? "border-blue-500 text-blue-500"
          : "border-transparent hover:bg-slate-50",
      )}
    >
      <Link
        to={`?view=${encodeURIComponent(userView)}`}
        className="w-full h-full"
      >
        {userView}
      </Link>
    </li>
  );
}
